import React, { useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import { useDispatch, useSelector } from 'react-redux';
import { getTruckTires, getTruckTireCategories, getTruckTireBrands } from '../../redux/actions/truckTire';
import TruckTireCard from '../../components/TruckTireCard';
import { Spin, Breadcrumb, Pagination, Select, Button, Input, Tag, Row, Col, Card, Checkbox } from 'antd';
import { Helmet } from 'react-helmet-async';
import { FilterOutlined, ClearOutlined, HomeOutlined, TruckOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { TruckTire } from '../../services/truckTireService';
import { RootState } from '../../redux/store';

const { Option } = Select;

const TruckTiresPage = () => {
    const { truckTires, categories, brands, pagination, loading } = useSelector((state: RootState) => state.truckTires);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    // Filter states
    const [filters, setFilters] = useState<{
        grup: string[];
        markasi: string[];
        search: string;
        page: number;
        limit: number;
        sortBy: string;
        sortOrder: 'asc' | 'desc';
    }>({
        grup: [],
        markasi: [],
        search: '',
        page: 1,
        limit: 12,
        sortBy: 'MalzemeAdi',
        sortOrder: 'asc'
    });

    // URL parametrelerini oku ve filtreleri uygula
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const search = searchParams.get('search');
        const grup = searchParams.getAll('grup');
        const markasi = searchParams.getAll('markasi');
        const page = searchParams.get('page');
        const sortBy = searchParams.get('sortBy');
        const sortOrder = searchParams.get('sortOrder') as 'asc' | 'desc';

        if (search || grup.length > 0 || markasi.length > 0 || page || sortBy || sortOrder) {
            setFilters(prev => ({
                ...prev,
                search: search || '',
                grup: grup,
                markasi: markasi,
                page: page ? parseInt(page) : 1,
                sortBy: sortBy || 'MalzemeAdi',
                sortOrder: sortOrder || 'asc'
            }));

            // API'ye filtrelerle birlikte istek gönder
            const apiParams: any = {
                page: page ? parseInt(page) : 1,
                limit: 12
            };

            if (search) apiParams.search = search;
            if (grup.length > 0) apiParams.grup = grup; // Multiple kategori seçimi
            if (markasi.length > 0) apiParams.markasi = markasi; // Multiple marka seçimi
            if (sortBy) apiParams.sortBy = sortBy;
            if (sortOrder) apiParams.sortOrder = sortOrder;

            dispatch(getTruckTires(apiParams));
        } else {
            // Varsayılan yükleme
            dispatch(getTruckTires({ page: 1, limit: 12 }));
        }

        // Filtreleme seçeneklerini yükle
        dispatch(getTruckTireCategories());
        dispatch(getTruckTireBrands());
    }, [dispatch, location.search]);

    // Filtreleri uygula
    const applyFilters = () => {
        const searchParams = new URLSearchParams();

        if (filters.search) searchParams.append('search', filters.search);
        if (filters.grup.length > 0) {
            filters.grup.forEach(g => searchParams.append('grup', g));
        }
        if (filters.markasi.length > 0) {
            filters.markasi.forEach(m => searchParams.append('markasi', m));
        }
        if (filters.sortBy !== 'MalzemeAdi') searchParams.append('sortBy', filters.sortBy);
        if (filters.sortOrder !== 'asc') searchParams.append('sortOrder', filters.sortOrder);

        // Filtreleme yapıldığında sayfa numarasını 1'e sıfırla
        if (filters.page !== 1) {
            setFilters(prev => ({ ...prev, page: 1 }));
        }

        navigate(`/kamyon-lastikleri?${searchParams.toString()}`);
    };

    // Filtreleri temizle
    const clearFilters = () => {
        setFilters({
            grup: [],
            markasi: [],
            search: '',
            page: 1,
            limit: 12,
            sortBy: 'MalzemeAdi',
            sortOrder: 'asc'
        });
        navigate('/kamyon-lastikleri');
    };

    // Sayfa değiştir
    const onPageChange = (page: number) => {
        setFilters(prev => ({ ...prev, page }));
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('page', page.toString());
        navigate(`/kamyon-lastikleri?${searchParams.toString()}`);
    };

    // Kamyon lastiği kartına tıklama
    const handleTruckTireClick = (truckTire: TruckTire) => {
        navigate(`/kamyon-lastikleri/${truckTire._id}`, {
            state: { tire: truckTire }
        });
    };

    if (loading && !truckTires?.length) {
        return (
            <MainLayout>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: '16px' }}>Kamyon lastikleri yükleniyor...</p>
                </div>
            </MainLayout>
        );
    }

    const hasActiveFilters = filters.search || filters.grup.length > 0 || filters.markasi.length > 0;

    return (
        <MainLayout>
            <Helmet>
                <title>Kamyon Lastikleri | Sakarya Lastik</title>
                <meta name="description" content="Ağır vasıta kamyon lastikleri. Asfalt, hafriyat ve her türlü kullanım için profesyonel kamyon lastikleri." />
                <meta name="keywords" content="kamyon lastiği, ağır vasıta lastiği, kamyon lastik fiyatları" />
            </Helmet>

            <div className="container mx-auto px-4 py-6">
                {/* Breadcrumb */}
                <Breadcrumb className="mb-4">
                    <Breadcrumb.Item href="/">
                        <HomeOutlined /> Ana Sayfa
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <TruckOutlined /> Kamyon Lastikleri
                    </Breadcrumb.Item>
                </Breadcrumb>

                {/* Başlık */}
                <div className="text-center mb-6">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        <TruckOutlined className="mr-3" />
                        Kamyon Lastikleri
                    </h1>
                    <p className="text-gray-600">
                        Ağır vasıta lastikleri - Profesyonel kalite, güvenilir performans
                    </p>
                </div>

                <Row gutter={24}>
                    {/* Sol Taraf - Filtreler */}
                    <Col xs={24} lg={6}>
                        <Card
                            title={
                                <span>
                                    <FilterOutlined className="mr-2" />
                                    Filtreler
                                </span>
                            }
                            className="mb-6"
                            extra={
                                hasActiveFilters && (
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<ClearOutlined />}
                                        onClick={clearFilters}
                                    >
                                        Temizle
                                    </Button>
                                )
                            }
                        >
                            <div className="space-y-4">
                                {/* Arama */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Lastik Ara</label>
                                    <Input.Search
                                        placeholder="Lastik modeli ara..."
                                        value={filters.search}
                                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                        onSearch={() => applyFilters()}
                                        allowClear
                                    />
                                </div>

                                {/* Kategoriler */}
                                {categories?.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            Kategoriler ({categories.length})
                                        </label>
                                        <Checkbox.Group
                                            value={filters.grup}
                                            onChange={(values) => {
                                                const newFilters = { ...filters, grup: values as string[], page: 1 };
                                                setFilters(newFilters);

                                                // Hemen filtreleri uygula
                                                const searchParams = new URLSearchParams();
                                                if (newFilters.search) searchParams.append('search', newFilters.search);
                                                if (newFilters.grup.length > 0) {
                                                    newFilters.grup.forEach(g => searchParams.append('grup', g));
                                                }
                                                if (newFilters.markasi.length > 0) {
                                                    newFilters.markasi.forEach(m => searchParams.append('markasi', m));
                                                }
                                                if (newFilters.sortBy !== 'MalzemeAdi') searchParams.append('sortBy', newFilters.sortBy);
                                                if (newFilters.sortOrder !== 'asc') searchParams.append('sortOrder', newFilters.sortOrder);

                                                navigate(`/kamyon-lastikleri?${searchParams.toString()}`);
                                            }}
                                            className="flex flex-col space-y-2"
                                        >
                                            {categories.map(category => (
                                                <Checkbox key={category.kategori} value={category.kategori}>
                                                    <span className="text-sm">
                                                        {category.kategori} ({category.urunSayisi})
                                                    </span>
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                )}

                                {/* Markalar */}
                                {brands?.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            Markalar ({brands.length})
                                        </label>
                                        <Checkbox.Group
                                            value={filters.markasi}
                                            onChange={(values) => {
                                                const newFilters = { ...filters, markasi: values as string[], page: 1 };
                                                setFilters(newFilters);

                                                // Hemen filtreleri uygula
                                                const searchParams = new URLSearchParams();
                                                if (newFilters.search) searchParams.append('search', newFilters.search);
                                                if (newFilters.grup.length > 0) {
                                                    newFilters.grup.forEach(g => searchParams.append('grup', g));
                                                }
                                                if (newFilters.markasi.length > 0) {
                                                    newFilters.markasi.forEach(m => searchParams.append('markasi', m));
                                                }
                                                if (newFilters.sortBy !== 'MalzemeAdi') searchParams.append('sortBy', newFilters.sortBy);
                                                if (newFilters.sortOrder !== 'asc') searchParams.append('sortOrder', newFilters.sortOrder);

                                                navigate(`/kamyon-lastikleri?${searchParams.toString()}`);
                                            }}
                                            className="flex flex-col space-y-2"
                                        >
                                            {brands.map(brand => (
                                                <Checkbox key={brand.marka} value={brand.marka}>
                                                    <span className="text-sm">
                                                        {brand.marka} ({brand.urunSayisi})
                                                    </span>
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                )}

                                {/* Sıralama */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Sıralama</label>
                                    <Select
                                        value={`${filters.sortBy}_${filters.sortOrder}`}
                                        onChange={(value) => {
                                            const [sortBy, sortOrder] = value.split('_');
                                            setFilters(prev => ({
                                                ...prev,
                                                sortBy,
                                                sortOrder: sortOrder as 'asc' | 'desc'
                                            }));
                                            setTimeout(() => applyFilters(), 100);
                                        }}
                                        className="w-full"
                                    >
                                        <Option value="MalzemeAdi_asc">İsim (A-Z)</Option>
                                        <Option value="MalzemeAdi_desc">İsim (Z-A)</Option>
                                        <Option value="HavaleFiyati_asc">Fiyat (Düşük-Yüksek)</Option>
                                        <Option value="HavaleFiyati_desc">Fiyat (Yüksek-Düşük)</Option>
                                        <Option value="StokMiktari_desc">Stok (Fazla-Az)</Option>
                                    </Select>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    {/* Sağ Taraf - Ürünler */}
                    <Col xs={24} lg={18}>
                        {/* Sonuç Bilgisi */}
                        {pagination && (
                            <div className="mb-4 flex justify-between items-center">
                                <span className="text-gray-600">
                                    Toplam {pagination.totalItems} ürün bulundu
                                </span>
                                {hasActiveFilters && (
                                    <div className="flex flex-wrap gap-1">
                                        {filters.search && (
                                            <Tag closable onClose={() => {
                                                setFilters(prev => ({ ...prev, search: '' }));
                                                setTimeout(() => applyFilters(), 100);
                                            }}>
                                                Arama: {filters.search}
                                            </Tag>
                                        )}
                                        {filters.grup.map(g => (
                                            <Tag key={g} closable onClose={() => {
                                                setFilters(prev => ({
                                                    ...prev,
                                                    grup: prev.grup.filter(item => item !== g)
                                                }));
                                                setTimeout(() => applyFilters(), 100);
                                            }}>
                                                Kategori: {g}
                                            </Tag>
                                        ))}
                                        {filters.markasi.map(m => (
                                            <Tag key={m} closable onClose={() => {
                                                setFilters(prev => ({
                                                    ...prev,
                                                    markasi: prev.markasi.filter(item => item !== m)
                                                }));
                                                setTimeout(() => applyFilters(), 100);
                                            }}>
                                                Marka: {m}
                                            </Tag>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Ürün Listesi */}
                        {loading ? (
                            <div className="text-center py-12">
                                <Spin size="large" />
                                <p className="mt-4 text-gray-600">Kamyon lastikleri yükleniyor...</p>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-4">
                                {truckTires?.map((tire: TruckTire) => (
                                    <TruckTireCard
                                        key={tire._id}
                                        truckTire={tire}
                                        onClick={handleTruckTireClick}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Ürün bulunamadı mesajı */}
                        {!loading && (!truckTires || truckTires.length === 0) && (
                            <div className="text-center py-12">
                                <TruckOutlined className="text-6xl text-gray-300 mb-4" />
                                <h3 className="text-xl text-gray-600 mb-2">
                                    Aradığınız kriterlere uygun kamyon lastiği bulunamadı
                                </h3>
                                <Button type="primary" onClick={clearFilters}>
                                    Filtreleri Temizle
                                </Button>
                            </div>
                        )}

                        {/* Sayfalama */}
                        {pagination && pagination.totalPages > 1 && (
                            <div className="flex justify-center mt-8">
                                <Pagination
                                    current={pagination.currentPage}
                                    total={pagination.totalItems}
                                    pageSize={pagination.itemsPerPage}
                                    onChange={onPageChange}
                                    showSizeChanger={false}
                                    showQuickJumper
                                    showTotal={(total, range) =>
                                        `${range[0]}-${range[1]} / ${total} ürün`
                                    }
                                />
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        </MainLayout>
    );
};

export default TruckTiresPage;
